// A hub's agent activity, from its curator's side: the Interactions that wait
// on the curator, on top, then every agent action newest first, each opening
// its curated transcript in the docked panel, then the Interactions that wait
// on no one. Studio shows what the Exchange's agent routes return and nothing
// of the platform's own: no playbook text, no gateway or retry
// internals. See Design > Hub agent, "Hosted" and "Pausing for the curator",
// and Design > UI, "Studio hierarchy" and "Workspace".
//
// The views below take what was read as props and hold no state, so each
// state is tested by calling it; the page and the reply form hold the state.

import type { ComponentChildren, VNode } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Button, ChoiceGroup, Dialog, DockPanel, EmptyState, Field, fmtNum, Icon, LinkButton, Notice, PageBar, StatusBadge, Textarea, Workspace } from "../../../../ui/src/index.ts";
import {
  agentCost, approvedChange, checkDraft, choiceCount, chosenLabels, closedInteractions, emptyDraft, interactionOf, jsonText, kindWords, noteRequired, priceDirection,
  PURPOSE_WORDS, STATE_WORDS, STATUS_TONES, STATUS_WORDS, timeline, waitingInteractions,
  type ActionId, type AgentActionTranscript, type AgentActionView, type AgentActivityResponse, type DraftQuestion, type InteractionState, type InteractionView,
  type PriceSuggestion, type ReplyDraft, type ReplySchema, type Subject, type SubjectKind, type TranscriptItem,
} from "../../domain/agent.ts";
import { hubTokenView } from "../../domain/hub-token.ts";
import { perMillion } from "../../domain/money.ts";
import { refusalWords } from "../../domain/toolsets.ts";
import type { ApiFailure, HubRecord, HubSummary } from "../../domain/types.ts";
import { describeFailure } from "../../api.ts";
import { agentActionKey, connectHubToken, disconnectHubToken, loadAgent, loadAgentAction, loadHubSecrets, replyToInteraction } from "../actions.ts";
import { Failed, Loading, Section } from "../parts.tsx";
import { href } from "../router.ts";
import { useHubLabel } from "../hubData.ts";
import { useStore, type Load } from "../store.ts";
import { capitalize, day, dayTime, OUTCOME_WORDS } from "../words.ts";

/**
 * An intent's label and Question page, where the hub's manifest has it; and
 * the hub's Toolsets page, where a toolset's card is, when the hub is known.
 */
export type IntentLink = ((intent: string) => { label: string; href?: string }) & { readonly toolsets?: string };
export const intentLinkOf = (hub: HubSummary | undefined): IntentLink => Object.assign((id: string) => {
  const intent = hub?.manifest.intents.find((i) => i.id === id);
  return intent && hub ? { label: capitalize(intent.label), href: href({ name: "question", hub: hub.hub.id, intent: id }) } : { label: id };
}, hub ? { toolsets: href({ name: "toolsets", hub: hub.hub.id }) } : {});

/** A toolset an inform names, by its commit, linked to its card on the hub's Toolsets page when Studio knows the hub. */
export function ToolsetCard({ commit, toolsets }: { commit: string; toolsets: string | undefined }) {
  const name = <>Toolset <code>{commit.slice(0, 12)}</code></>;
  return <p class="st-agent-text">{toolsets ? <a href={toolsets}>{name}</a> : name}</p>;
}

/**
 * The Exchange has no hub agent in this environment: its 503 with reason
 * NotConfigured, which trying again won't change. Any other 503 is an outage
 * that may pass, shown with Try again.
 */
export const agentUnavailable = (e: ApiFailure) => e.status === 503 && e.reason === "NotConfigured";

const IntentName = ({ link }: { link: { label: string; href?: string } }) => (link.href ? <a href={link.href}>{link.label}</a> : <span>{link.label}</span>);

/**
 * A price suggestion: the price now and the one suggested, the evidence the
 * hub agent read, and why. "Priced out" is the intent's PriceTooLow count in
 * the window: questions the hub turned away because they bid below its ask.
 */
export function PriceCard({ s, intent }: { s: PriceSuggestion; intent: IntentLink }) {
  const direction = priceDirection(s.currentAskUsd, s.proposedAskUsd);
  const e = s.evidence;
  return <div class="st-agent-price">
    <p class="st-agent-price__move"><IntentName link={intent(s.intent)} />
      <span class="st-agent-price__prices">{perMillion(s.currentAskUsd)} <Icon name="arrow" size={16} aria-hidden="true" /> <strong>{perMillion(s.proposedAskUsd)}</strong></span>
      {direction !== "keep" && <StatusBadge tone="info">{direction === "lower" ? "Lower" : "Raise"}</StatusBadge>}</p>
    <dl class="st-facts st-agent-evidence">
      <div><dt>Priced out</dt><dd>{fmtNum(e.declined)} {e.declined === 1 ? "question" : "questions"} in {e.windowDays} {e.windowDays === 1 ? "day" : "days"}</dd></div>
      <div><dt>Median bid</dt><dd>{e.medianBidUsd === null ? "–" : perMillion(e.medianBidUsd)}</dd></div>
      <div><dt>Highest bid</dt><dd>{e.maxBidUsd === null ? "–" : perMillion(e.maxBidUsd)}</dd></div>
    </dl>
    {s.rationale && <p class="st-agent-text">{s.rationale}</p>}
  </div>;
}

// A draft question's expected outcome, in the words Studio shows outcomes in.
const EXPECTED_WORDS = {
  Answer: OUTCOME_WORDS.answered, NotFound: OUTCOME_WORDS.notFound, MissingParameters: OUTCOME_WORDS.detail, OutOfScope: OUTCOME_WORDS.declined,
} satisfies Record<DraftQuestion["expectOutcome"], string>;

/** A draft question set: each question, numbered, with the outcome it expects, as a golden question names it. */
export function QuestionSetCard({ questions }: { questions: ReadonlyArray<DraftQuestion> }) {
  return <ol class="st-agent-questions">{questions.map((q, i) => <li key={i}>
    <span class="st-agent-text">{q.question}</span> <span class="st-note">Expects: {EXPECTED_WORDS[q.expectOutcome]}</span>
  </li>)}</ol>;
}

// Each subject kind's view, by kind, so a kind the contract adds fails to compile until it has one.
type SubjectOf = { [S in Subject as S["kind"]]: S };
type SubjectViews = { [K in SubjectKind]: (s: SubjectOf[K], intent: IntentLink) => VNode };
const SUBJECT_VIEWS: SubjectViews = {
  priceChange: (s, intent) => <PriceCard s={s.price} intent={intent} />,
  questionSet: (s) => <QuestionSetCard questions={s.questions} />,
  toolset: (s, intent) => <ToolsetCard commit={s.commit} toolsets={intent.toolsets} />,
};
const subjectView = <K extends SubjectKind>(kind: K, s: SubjectOf[K], intent: IntentLink) => SUBJECT_VIEWS[kind](s, intent);
/** The work an Interaction is about. A deploy-order window can still deliver a kind this build has no view for: it shows as JSON. */
export function SubjectBlock({ subject, intent }: { subject: Subject; intent: IntentLink }) {
  if (!Object.hasOwn(SUBJECT_VIEWS, subject.kind)) return <pre class="st-agent-json">{JSON.stringify(subject, null, 2)}</pre>;
  return subjectView(subject.kind, subject, intent);
}

// What closed an Interaction no reply did, in words, with when.
type Closed = Exclude<InteractionState, "open" | "answered">;
const CLOSED_WORDS = {
  expired: (when) => `This expired ${when} before you replied, so your hub agent took nothing from it.`,
  superseded: (when, newerShown) => `Your hub agent replaced this with a newer one ${when}${newerShown ? ", shown above" : ""}. It takes no reply.`,
  cancelled: (when) => `Your hub agent withdrew this ${when}. It takes no reply.`,
} satisfies Record<Closed, (when: string, newerShown: boolean) => string>;
const closedWhen = (i: InteractionView) => dayTime(i.closedAt ?? i.expiresAt ?? i.at);

/**
 * An answered Interaction's receipts, in words. An approved change reads as
 * waiting until a realization reports it done: never "Done", and never a
 * change for the curator to make. See Design > Hub agent, "Pausing for the
 * curator".
 */
export function receiptWords(i: InteractionView): string[] {
  const done = i.receipts.done;
  return [
    ...(i.receipts.applied !== null ? [`Your hub agent took it up ${dayTime(i.receipts.applied)}.`] : []),
    ...(approvedChange(i) && done === null ? ["Approved, waiting to be carried out."] : []),
    ...(approvedChange(i) && done === null && i.approval !== null ? [`The approval holds until ${dayTime(i.approval.until)}.`] : []),
    ...(done !== null ? [done.ok ? `Carried out ${dayTime(done.at)}: ${done.detail}` : `Approved; carrying it out failed: ${done.detail}`] : []),
  ];
}

/** A Curator reply as the curator gave it: the options they chose, by label, their own answer and their note. */
export function ReplyText({ schema, reply }: { schema: ReplySchema | null; reply: { choice: readonly string[]; other?: string; note?: string } }) {
  const labels = chosenLabels(schema, reply.choice);
  const otherLabel = schema?.type === "choice" ? schema.other?.label || "Your own answer" : null;
  return <>
    {labels.length > 0 && <p class="st-agent-text"><strong>{labels.join(", ")}</strong></p>}
    {reply.other !== undefined && <p class="st-agent-text">{otherLabel ? <><strong>{otherLabel}:</strong> {reply.other}</> : reply.other}</p>}
    {reply.note !== undefined && <p class="st-agent-text"><strong>Note:</strong> {reply.note}</p>}
  </>;
}

/**
 * One Interaction: what it asks, how it stands and when, the prompt, the work
 * it's about, then by state: the reply form the page passes in while it's
 * open, the Curator reply and its receipts once answered, or what closed it.
 * An inform asks nothing, so it has no form, and no badge: the hub agent
 * keeps it answered as it opens it, with no reply, since it waits on no one.
 */
export function InteractionCard({ interaction: i, intent, replyForm, replyNote, newerShown = false }: {
  interaction: InteractionView; intent: IntentLink; replyForm?: ComponentChildren; replyNote?: string | undefined; newerShown?: boolean;
}) {
  const inform = i.purpose === "inform";
  const state = STATE_WORDS[i.state];
  return <article class="st-agent-interaction" aria-label={PURPOSE_WORDS[i.purpose]}>
    <div class="st-agent-interaction__head">
      <span class="st-agent-interaction__what"><strong>{PURPOSE_WORDS[i.purpose]}</strong>{!inform && <StatusBadge tone={state.tone}>{state.label}</StatusBadge>}</span>
      <span class="st-note">{dayTime(i.at)}{i.state === "open" && i.expiresAt !== null ? ` · open until ${dayTime(i.expiresAt)}` : ""}</span>
    </div>
    {i.prompt && <p class="st-agent-text">{i.prompt}</p>}
    {i.subject && <SubjectBlock subject={i.subject} intent={intent} />}
    {outcomeOf(i, replyForm, replyNote, newerShown)}
  </article>;
}

function outcomeOf(i: InteractionView, replyForm: ComponentChildren, replyNote: string | undefined, newerShown: boolean): ComponentChildren {
  switch (i.state) {
    case "open":
      if (i.purpose === "inform") return null;
      return i.replySchema === null ? <p class="st-note">This takes no reply in Studio.</p> : replyForm;
    case "answered": return i.reply && <div class="st-agent-reply">
      <p class="st-caption">You replied {dayTime(i.reply.at)}</p>
      <ReplyText schema={i.replySchema} reply={i.reply} />
      {receiptWords(i).map((w) => <p key={w} class="st-note">{w}</p>)}
      {replyNote && <p class="st-note">{replyNote}</p>}
    </div>;
    case "expired":
    case "superseded":
    case "cancelled":
      return <p class="st-note st-agent-closed">{CLOSED_WORDS[i.state](closedWhen(i), newerShown)}</p>;
    default:
      return i.state satisfies never;
  }
}

/**
 * The reply form's fields for one reply schema, from the draft the form
 * holds: a text box for text; for a choice, radio buttons for one, or
 * checkboxes for several, the suggested option marked and never chosen for
 * the curator; a field for their own answer where the schema takes one; and
 * always a note, required when a chosen option asks for one. Send is enabled only
 * when checkReply takes the draft, the same check the hub agent runs, and
 * otherwise the form shows checkReply's words. See Design > Hub agent,
 * "Pausing for the curator".
 */
export function ReplyFields({ id, schema, draft, onChange, onSend, busy = false, error }: {
  id: string; schema: ReplySchema; draft: ReplyDraft; onChange: (d: ReplyDraft) => void; onSend: () => void; busy?: boolean; error?: string | null;
}) {
  const checked = checkDraft(schema, draft);
  const foot = <div class="st-agent-replybox__foot">
    <span class="st-note" aria-live="polite">{checked.ok ? "" : checked.detail}</span>
    <Button size="sm" variant="solid" disabled={!checked.ok || busy} onClick={onSend}>{busy ? "Sending…" : "Send reply"}</Button>
  </div>;
  const failed = error ? <p class="ui-field__error" role="alert">{error}</p> : null;
  switch (schema.type) {
    case "text": return <div class="st-agent-replybox">
      <Field id={`${id}-text`} label="Your reply" hint="Your hub agent reads it next.">
        {(attrs) => <Textarea {...attrs} rows={3} maxLength={schema.maxLength} value={draft.other} onInput={(e) => onChange({ ...draft, other: e.currentTarget.value })} />}
      </Field>
      <span class="st-note">{fmtNum(draft.other.trim().length)} / {fmtNum(schema.maxLength)}</span>
      {noteField(id, false, draft, onChange)}
      {failed}{foot}
    </div>;
    case "choice": {
      const required = noteRequired(schema, draft.choice);
      const { other } = schema;
      return <div class="st-agent-replybox">
        <ChoiceGroup legend="Your answer" hint={choiceCount(schema)} multiple={schema.max > 1} value={draft.choice} onChange={(choice) => onChange({ ...draft, choice })}
          options={schema.options.map((o) => ({ value: o.key, label: o.label, ...(o.detail ? { detail: o.detail } : {}), ...(o.key === schema.recommended ? { badge: <StatusBadge tone="info">Suggested</StatusBadge> } : {}) }))} />
        {schema.max === 1 && schema.min === 0 && draft.choice.length > 0 && <LinkButton onClick={() => onChange({ ...draft, choice: [] })}>Clear your choice</LinkButton>}
        {other && <Field id={`${id}-other`} label={other.label || "Your own answer"} {...(other.exclusive ? { hint: "Instead of an option above: leave them all unchosen." } : {})}>
          {(attrs) => <Textarea {...attrs} rows={2} maxLength={other.maxLength} value={draft.other} onInput={(e) => onChange({ ...draft, other: e.currentTarget.value })} />}
        </Field>}
        {noteField(id, required, draft, onChange)}
        {failed}{foot}
      </div>;
    }
    default: return schema satisfies never;
  }
}

// The note every reply form has, marked required when a chosen option asks for one.
function noteField(id: string, required: boolean, draft: ReplyDraft, onChange: (d: ReplyDraft) => void) {
  return <Field id={`${id}-note`} label={required ? "Note (required)" : "Note (optional)"} hint={required ? "Say why. Your hub agent reads it next." : "Anything your hub agent should know."}>
    {(attrs) => <Textarea {...attrs} rows={2} maxLength={4000} required={required} value={draft.note} onInput={(e) => onChange({ ...draft, note: e.currentTarget.value })} />}
  </Field>;
}

/** The reply form under a waiting Interaction: it holds the draft, and sends the reply checkReply normalized. */
function ReplyForm({ hub, interaction, schema }: { hub: HubRecord; interaction: InteractionView; schema: ReplySchema }) {
  const [draft, setDraft] = useState<ReplyDraft>(() => emptyDraft(schema));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    const checked = checkDraft(schema, draft);
    if (!checked.ok) return;
    setBusy(true);
    setError(null);
    const r = await replyToInteraction(hub.id, interaction.interactionId, checked.reply);
    setBusy(false);
    // A sent reply reads the activity again, which shows it in place of this form.
    if (!r.ok) setError(replyFailureWords(r.error));
  };
  return <ReplyFields id={`st-reply-${interaction.interactionId}`} schema={schema} draft={draft} onChange={setDraft} onSend={() => void send()} busy={busy} error={error} />;
}

// Why a reply to an Interaction that closed first wasn't kept, from the state the Exchange returned it in.
const CLOSED_BEFORE = {
  open: "read the activity again to see how it stands.",
  answered: "it already has a reply, shown on the page.",
  expired: "it expired.",
  superseded: "your hub agent replaced it with a newer one.",
  cancelled: "your hub agent withdrew it.",
} satisfies Record<InteractionState, string>;

/**
 * Why a reply wasn't kept, in words. The activity is read again either way,
 * so an answer kept elsewhere shows. Read by the failure's tag, which the
 * contract's client decodes: the route answers 409 for an archived hub as
 * well as for a closed Interaction, and 404 for a hub as well as for an
 * Interaction.
 */
export function replyFailureWords(e: ApiFailure): string {
  const words = REPLY_FAILURE_WORDS.get(e._tag);
  if (words !== undefined) return words(e);
  if (agentUnavailable(e)) return "The hub agent isn't available in this environment, so the reply wasn't sent.";
  return describeFailure(e);
}
// A failure's tag is open text on the wire, so this is a lookup; a tag it lacks is described as any other failure.
const REPLY_FAILURE_WORDS = new Map<string, (e: ApiFailure) => string>([
  ["BadRequest", () => "The Exchange couldn't read that reply. Read the activity again and send it once more."],
  ["ReplyInvalid", (e) => e.detail ?? "That reply doesn't fit what was asked."],
  ["InteractionClosed", (e) => `This closed before your reply: ${e.closedAs ? CLOSED_BEFORE[e.closedAs] : CLOSED_BEFORE.open}`],
  ["HubArchived", () => "This hub is archived, so its hub agent takes no replies."],
  ["NotRepoConfigurer", () => "Only the hub's curators can reply to its hub agent."],
  ["NotFound", () => "Your hub agent no longer has this. Read the activity again."],
]);

/** The agent actions, newest first; selecting one opens its transcript. */
export function Timeline({ actions, selected, onSelect }: { actions: ReadonlyArray<AgentActionView>; selected: ActionId | null; onSelect: (actionId: ActionId) => void }) {
  return <ol class="st-agent-timeline">{timeline(actions).map((a) => <li key={a.actionId}>
    <button type="button" class="st-agent-action" aria-current={a.actionId === selected ? "true" : undefined} onClick={() => onSelect(a.actionId)}>
      <span class="st-agent-action__head">
        <strong>{kindWords(a.kind)}</strong>
        <StatusBadge tone={STATUS_TONES[a.status]}>{STATUS_WORDS[a.status]}</StatusBadge>
        <span class="st-note st-when">{dayTime(a.startedAt)}</span>
      </span>
      <span class="st-agent-text">{a.summary ?? (a.status === "running" ? "Still working." : "No summary.")}</span>
      <span class="st-note st-agent-action__meta"><span class="st-id">{a.model}</span> · {agentCost(a.costUsd)}</span>
    </button>
  </li>)}</ol>;
}

/**
 * While a daily spend limit pauses the hub agent, the note that says so,
 * from the read's pausedUntil, a UTC midnight; or nothing. It never says
 * whose limit, the hub's or every hub's together. See Design > UI, "Agent
 * activity".
 */
export function pausedNote(activity: AgentActivityResponse, readAt: number) {
  if (activity.pausedUntil === null || activity.pausedUntil <= readAt) return null;
  return <Notice tone="warning" title="Paused until midnight UTC: daily spend limit reached">Your hub agent picks up again then. Nothing waiting for it is lost.</Notice>;
}

/** The page's work for one read of the activity: its loading, unavailable, failed, empty and read states. */
export function ActivityBody({ load, retry, intent, selected, onSelect, replyForm, replyNote = () => undefined }: {
  load: Load<AgentActivityResponse> | undefined; retry: () => void; intent: IntentLink;
  selected: ActionId | null; onSelect: (actionId: ActionId) => void; replyForm: (interaction: InteractionView, schema: ReplySchema) => ComponentChildren;
  replyNote?: (interaction: InteractionView) => string | undefined;
}) {
  if (!load || load.status === "loading") return <Loading label="Loading agent activity" />;
  if (load.status === "failed") return agentUnavailable(load.error)
    ? <Notice tone="neutral" title="Your hub agent isn't on for this hub">Its activity shows here once the hub agent is turned on for your hub.</Notice>
    : <Failed what="the agent activity" error={load.error} retry={retry} />;
  const { actions, interactions } = load.value;
  const paused = pausedNote(load.value, load.at);
  if (actions.length === 0 && interactions.length === 0) return <>{paused}<EmptyState>Your hub agent hasn't done anything yet. It starts when something happens on your hub, such as a staged toolset, and on its regular reviews.</EmptyState></>;
  const waiting = waitingInteractions(interactions);
  const closed = closedInteractions(interactions);
  const shown = new Set(interactions.map((i) => i.interactionId));
  return <>
    {paused}
    {waiting.length > 0 && <Section title="Waiting for you" note="Your hub agent asks, and you decide. Nothing it asks about changes until you reply.">
      <div class="st-agent-interactions">{waiting.map((i) => <InteractionCard key={i.interactionId} interaction={i} intent={intent} replyForm={i.replySchema && replyForm(i, i.replySchema)} />)}</div>
    </Section>}
    <Section title="Activity" note="Each time your hub agent worked, newest first. Select one to read what it did.">
      {actions.length ? <Timeline actions={actions} selected={selected} onSelect={onSelect} /> : <EmptyState>No agent actions yet.</EmptyState>}
    </Section>
    {closed.length > 0 && <Section title="Earlier" note="What your hub agent asked or told you before, and how each one ended.">
      <div class="st-agent-interactions">{closed.map((i) => <InteractionCard key={i.interactionId} interaction={i} intent={intent} replyNote={replyNote(i)}
        newerShown={i.supersededBy !== null && shown.has(i.supersededBy)} />)}</div>
    </Section>}
  </>;
}

/** One transcript item: what the agent said, a tool it called and what came back, an Interaction it opened or a Curator reply. */
export function TranscriptEntry({ item, interactions, intent }: { item: TranscriptItem; interactions: ReadonlyArray<InteractionView>; intent: IntentLink }) {
  switch (item.type) {
    case "agent": return <li class="st-agent-item"><p class="st-caption">Hub agent · {dayTime(item.at)}{item.aborted ? " · cut off" : ""}</p><p class="st-agent-text">{item.text}</p></li>;
    case "tool-call": return <li class="st-agent-item"><p class="st-caption">{item.aborted ? <>Cut off; <span class="st-id">{item.tool}</span> didn't run</> : <>Used <span class="st-id">{item.tool}</span></>}</p><pre class="st-agent-json">{jsonText(item.args)}</pre></li>;
    case "tool-result": return <li class="st-agent-item"><details class="st-agent-result">
      <summary><span class="st-id">{item.tool}</span> {item.ok ? "returned" : "failed"}{item.truncated ? " · cut short" : ""}</summary>
      <pre class="st-agent-json">{jsonText(item.output)}</pre>
      {item.truncated && <p class="st-caption">Cut to its first 8,000 characters.</p>}
    </details></li>;
    case "interaction": {
      const i = interactionOf(item, interactions);
      return <li class="st-agent-item">{i ? <InteractionCard interaction={i} intent={intent} replyForm={<p class="st-note">Reply at the top of the page.</p>} />
        : <p class="st-note">Asked or told you something that's no longer among the newest.</p>}</li>;
    }
    case "reply": {
      // Labels where the Interaction is in view; otherwise the reply's keys as they are.
      const i = interactionOf(item, interactions);
      return <li class="st-agent-item st-agent-reply"><p class="st-caption">You replied · {dayTime(item.at)}</p><ReplyText schema={i?.replySchema ?? null} reply={item.reply} /></li>;
    }
    default: return item satisfies never;
  }
}

/** An agent action's totals and curated transcript, oldest item first. */
export function TranscriptBody({ load, interactions, intent, retry }: { load: Load<AgentActionTranscript> | undefined; interactions: ReadonlyArray<InteractionView>; intent: IntentLink; retry: () => void }) {
  if (!load || load.status === "loading") return <Loading label="Loading the transcript" />;
  if (load.status === "failed") return agentUnavailable(load.error)
    ? <Notice tone="neutral" title="The hub agent isn't available in this environment" />
    : <Failed what="this agent action" notFound="this agent action" error={load.error} retry={retry} />;
  const { action: a, items } = load.value;
  return <>
    <h2>{kindWords(a.kind)}</h2>
    <p><StatusBadge tone={STATUS_TONES[a.status]}>{STATUS_WORDS[a.status]}</StatusBadge> <span class="st-note">{dayTime(a.startedAt)}{a.finishedAt !== null ? ` to ${dayTime(a.finishedAt)}` : ""}</span></p>
    <dl class="st-facts">
      <div><dt>Model</dt><dd class="st-id">{a.model}</dd></div>
      <div><dt>Model calls</dt><dd>{fmtNum(a.modelCalls)}</dd></div>
      <div><dt>Tokens</dt><dd>{fmtNum(a.inputTokens)} in · {fmtNum(a.outputTokens)} out</dd></div>
      <div><dt>Cost</dt><dd>{agentCost(a.costUsd)}</dd></div>
    </dl>
    {a.summary && <p class="st-agent-text">{a.summary}</p>}
    <h3 class="st-agent-transcript__title">Transcript</h3>
    {items.length ? <ol class="st-agent-transcript">{items.map((item, i) => <TranscriptEntry key={i} item={item} interactions={interactions} intent={intent} />)}</ol>
      : <EmptyState>Nothing in this transcript yet.</EmptyState>}
  </>;
}

/** The selected agent action's panel: reads its transcript, again whenever the activity is read again. */
function TranscriptPanel({ hub, actionId, interactions, intent, readAt, onClose }: { hub: HubRecord; actionId: ActionId; interactions: ReadonlyArray<InteractionView>; intent: IntentLink; readAt: number; onClose: () => void }) {
  const load = useStore((s) => s.agentActions[agentActionKey(hub.id, actionId)]);
  useEffect(() => { void loadAgentAction(hub.id, actionId, true); }, [hub.id, actionId, readAt]);
  const head = <>
    <span>Agent action</span>
    <Button variant="ghost" size="sm" icon aria-label="Close the panel" onClick={onClose}><Icon name="close" size={16} /></Button>
  </>;
  return <DockPanel label="Agent action" head={head}>
    <TranscriptBody load={load} interactions={interactions} intent={intent} retry={() => void loadAgentAction(hub.id, actionId, true)} />
  </DockPanel>;
}

/**
 * Whether the hub agent can work: the hub's Hub token, which a curator who
 * configures the hub's repository connects with their own WarmHub access,
 * and disconnects. Connecting again renews it. An archived hub shows its
 * state only. See Design > Hub agent, "The credential path".
 */
function HubTokenPanel({ record }: { record: HubRecord }) {
  const load = useStore((s) => s.hubSecrets[record.id]);
  const [busy, setBusy] = useState<"connect" | "disconnect" | null>(null);
  const [problem, setProblem] = useState<{ which: "connect" | "disconnect"; text: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { setProblem(null); void loadHubSecrets(record.id, true); }, [record.id]);
  const change = async (which: "connect" | "disconnect") => {
    setBusy(which);
    setProblem(null);
    const r = await (which === "connect" ? connectHubToken(record.id) : disconnectHubToken(record.id));
    setBusy(null);
    if (!r.ok) setProblem({ which, text: refusalWords(which, r.error) });
  };
  if (!load || load.status === "loading") return <Section title="Your hub agent's access"><Loading label="Loading your hub agent's access" /></Section>;
  if (load.status === "failed") return <Section title="Your hub agent's access"><Failed what="your hub agent's access" notFound="this hub" error={load.error} retry={() => void loadHubSecrets(record.id, true)} /></Section>;
  const view = hubTokenView(load.value.hubToken, day);
  const archived = record.listing === "archived";
  return <Section title="Your hub agent's access" action={archived ? null : <>
    {view.connect && <Button size="sm" variant="solid" disabled={busy !== null} onClick={() => void change("connect")}>{busy === "connect" ? "Connecting…" : view.connect}</Button>}
    {view.connected && <Button size="sm" disabled={busy !== null} onClick={() => setConfirming(true)}>{busy === "disconnect" ? "Disconnecting…" : "Disconnect"}</Button>}
  </>}>
    <p><StatusBadge tone={view.tone}>{view.label}</StatusBadge> <span class="st-note">{view.text}</span></p>
    {problem && <Notice live tone="danger" title={problem.which === "connect" ? "Studio couldn't connect your hub agent" : "Studio couldn't disconnect your hub agent"}>{problem.text}</Notice>}
    <Dialog open={confirming} onClose={() => setConfirming(false)} title="Disconnect your hub agent?"
      description="It stops working on this hub until a curator connects it again. Your toolsets and what it already did stay as they are."
      actions={<><Button size="sm" onClick={() => setConfirming(false)}>Cancel</Button>
        <Button size="sm" variant="solid" onClick={() => { setConfirming(false); void change("disconnect"); }}>Disconnect</Button></>} />
  </Section>;
}

export function AgentActivity({ record, hub }: { record: HubRecord; hub?: HubSummary }) {
  const short = useHubLabel(record.name);
  const load = useStore((s) => s.agent[record.id]);
  const notes = useStore((s) => s.agentReplies);
  const [selected, setSelected] = useState<ActionId | null>(null);
  useEffect(() => { setSelected(null); void loadAgent(record.id, true); }, [record.id]);
  const intent = intentLinkOf(hub);
  const interactions = load?.status === "ok" ? load.value.interactions : [];
  const readAt = load?.status === "ok" ? load.at : 0;
  const crumbs = hub ? <a href={href({ name: "overview", hub: record.id })}>Hub overview</a> : <a href={href({ name: "toolsets", hub: record.id })}>Toolsets</a>;
  const panel = selected ? <TranscriptPanel hub={record} actionId={selected} interactions={interactions} intent={intent} readAt={readAt} onClose={() => setSelected(null)} /> : undefined;
  return <>
    <PageBar crumbs={crumbs} title={<>Agent activity for <strong>{short}</strong></>}>
      <Button size="sm" disabled={load?.status === "loading"} onClick={() => void loadAgent(record.id, true)}>Read again</Button>
    </PageBar>
    <Workspace panel={panel}>
      <HubTokenPanel record={record} />
      <ActivityBody load={load} retry={() => void loadAgent(record.id, true)} intent={intent} selected={selected} onSelect={setSelected}
        replyForm={(i, schema) => <ReplyForm key={i.interactionId} hub={record} interaction={i} schema={schema} />} replyNote={(i) => notes[`${record.id} ${i.interactionId}`]} />
    </Workspace>
  </>;
}
